import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import "./App.css";

import { MsalProvider } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";

import Routes from "./routes/Routes";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { RootState } from "./store/reducers";
import { getUser } from "./store/modules/user/userActions";
import { AppDispatch } from "./store/store";
import {
  getCommonData,
  getUserCustomData,
} from "./store/modules/common/commonActions";
import { getBrokerageAnniversaries } from "./store/modules/financialSettings/financialSettingsActions";
import { loadCoAgentFilter } from "./store/modules/coAgentFilter/coAgentFilterActions";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { getHiddenFields } from "./store/modules/hiddenFields/hiddenFieldActions";
import AuthenticationHandler from "./utils/authenticationHandler";
import ErrorBoundary from "./ui-components/ErrorBoundary";
import { hasActiveSubscription } from "./utils/subscriptionUtils";

type AppProps = {
  pca: IPublicClientApplication;
};

const muiCache = createCache({
  key: "mui",
  prepend: true,
});
const queryClient = new QueryClient();

function App({ pca }: AppProps) {
  // // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  // const navigate = useNavigate();
  // const navigationClient = new CustomNavigationClient(navigate);
  // pca.setNavigationClient(navigationClient);

  // const { instance, inProgress } = useMsal();
  const account = pca.getActiveAccount();

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const { loading: commonDataLoading, loaded: commonDataLoaded } = useSelector(
    (state: RootState) => state.commonData,
  );

  const { loading: userDataLoading, loaded: userDataLoaded } = useSelector(
    (state: RootState) => state.commonData.userData,
  );
  const dispatch = useDispatch<AppDispatch>();

  const { currentUser: user } = useSelector((state: RootState) => state?.user);

  const { loading: hiddenFieldsLoading, loaded: hiddenFieldsLoaded } =
    useSelector((state: RootState) => state?.hiddenFields);

  const { loading: brokerageLoading, loaded: brokerageLoaded } = useSelector(
    (state: RootState) => state.financialSettings.brokerageAnniversaries,
  );

  const hasFetchedRef = useRef(false);
  const hasCommonFetchedRef = useRef(false);

  useEffect(() => {
    if (account && isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(getUser());
    }
  }, [account, isAuthenticated, dispatch]);

  useEffect(() => {
    if (account && !commonDataLoading && !commonDataLoaded) {
      dispatch(getCommonData());
    }
  }, [dispatch, account, commonDataLoading, commonDataLoaded]);
  useEffect(() => {
    if (account && isAuthenticated && !hasCommonFetchedRef.current) {
      hasCommonFetchedRef.current = true;
      dispatch(getUserCustomData());
      dispatch(loadCoAgentFilter());
    }
  }, [dispatch, account, isAuthenticated]);

  useEffect(() => {
    if (account) {
      if (!hiddenFieldsLoading && !hiddenFieldsLoaded && isAuthenticated) {
        dispatch(getHiddenFields());
      }
    }
  }, [
    account,
    dispatch,
    isAuthenticated,
    hiddenFieldsLoading,
    hiddenFieldsLoaded,
  ]);

  useEffect(() => {
    if (
      user !== null &&
      user &&
      account &&
      isAuthenticated &&
      !brokerageLoaded &&
      !brokerageLoading &&
      hasActiveSubscription(user)
    ) {
      dispatch(getBrokerageAnniversaries());
    }
  }, [
    account,
    dispatch,
    isAuthenticated,
    user,
    brokerageLoaded,
    brokerageLoading,
  ]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={pca}>
          <CacheProvider value={muiCache}>
            <AuthenticationHandler />

            <Routes />
          </CacheProvider>
        </MsalProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
